import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { CheckCircle, XCircle, Clock, CreditCard, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Configure axios with backend URL
const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
axios.defaults.baseURL = `${API}/api`;

const MembershipRenewalResult = () => {
  const [renewalStatus, setRenewalStatus] = useState("loading");
  const [renewalData, setRenewalData] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const status = urlParams.get('status');

    if (status === 'cancelled') {
      setRenewalStatus('cancelled');
      return;
    }

    if (!sessionId) {
      setRenewalStatus('error');
      setError('No session information found');
      return;
    }

    // Check renewal status
    checkRenewalStatus(sessionId);
  }, []);

  const checkRenewalStatus = async (sessionId, attempt = 1) => {
    try {
      const response = await axios.get(`/membership/renewal/status/${sessionId}`);
      
      if (response.data.status === 'completed') {
        setRenewalStatus('success');
        setRenewalData(response.data);
      } else if (response.data.status === 'pending') {
        setRenewalStatus('pending');
        setRenewalData(response.data);
        if (attempt < 6) setTimeout(() => checkRenewalStatus(sessionId, attempt + 1), 3000);
      } else {
        setRenewalStatus('failed');
        setError(response.data.error_message || 'Renewal failed');
      }
    } catch (error) {
      console.error('Error checking renewal status:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setRenewalStatus('error');
      setError(error.response?.data?.detail || 'Failed to check renewal status');
    }
  };

  const getStatusIcon = () => {
    switch (renewalStatus) {
      case 'success':
        return <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />;
      case 'pending':
        return <Clock className="h-16 w-16 text-yellow-500 mx-auto" />;
      case 'cancelled':
        return <XCircle className="h-16 w-16 text-gray-500 mx-auto" />;
      default:
        return <XCircle className="h-16 w-16 text-red-500 mx-auto" />;
    }
  };

  const getStatusMessage = () => {
    switch (renewalStatus) {
      case 'success':
        return {
          title: 'Membership Renewed Successfully!',
          message: `Your membership has been renewed for ${renewalData?.renewal_year}. You can now continue booking events.`,
          color: 'text-green-700'
        };
      case 'pending':
        return {
          title: 'Renewal Processing',
          message: 'Your renewal payment is being processed. You will receive a confirmation email shortly.',
          color: 'text-yellow-700'
        };
      case 'cancelled':
        return {
          title: 'Renewal Cancelled',
          message: 'You cancelled the membership renewal process. You can try again when ready.',
          color: 'text-gray-700'
        };
      default:
        return {
          title: 'Renewal Failed',
          message: error || 'There was an issue with your membership renewal. Please try again or contact support.',
          color: 'text-red-700'
        };
    }
  };

  const statusMessage = getStatusMessage();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-4">
          <div className="mb-4">
            {getStatusIcon()}
          </div>
          <CardTitle className={`text-xl font-semibold ${statusMessage.color}`}>
            {statusMessage.title}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="text-center space-y-6">
          <p className="text-gray-600">
            {statusMessage.message}
          </p>

          {renewalStatus === 'success' && renewalData && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
              <h4 className="font-medium text-green-900 mb-2">Renewal Details:</h4>
              <div className="text-sm text-green-700 space-y-1">
                <div>Membership Year: {renewalData.renewal_year}</div>
                <div>Expires: December 31st, {renewalData.renewal_year}</div>
                <div data-testid="renewal-amount">Amount: €{Number(renewalData.amount || 0).toFixed(2)}</div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={() => navigate('/')} 
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Home className="h-4 w-4 mr-2" />
              Return to Dashboard
            </Button>
            
            {(renewalStatus === 'failed' || renewalStatus === 'cancelled') && (
              <Button 
                onClick={() => window.location.reload()} 
                variant="outline"
                className="flex-1"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MembershipRenewalResult;